import { DOCUMENT, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Fixture } from '@cds/betting-offer';
import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { LoggerFactory, SportsRemoteLogger } from '@frontend/sports/common/core/feature/logging';
import { UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { concat, uniqBy } from 'lodash-es';
import { Subject, debounceTime } from 'rxjs';

import { SchemaMarkupType } from './schema-markup-enum';
import {
    SeoGenericSchema,
    SeoLdJsonSchemaContainer,
    SeoMarkupConstants,
    SeoPlaceSchema,
    SeoPostalAddressSchema,
    SeoSportsEventSchema,
} from './schema-markup.models';

@Injectable({ providedIn: 'root' })
export class SchemaMarkupService {
    private urlHelper = inject(UrlHelperService);

    private readonly logger: SportsRemoteLogger;
    private events: WeakRef<EventModel | Fixture>[];
    private moduleEvents$ = new Subject<void>();
    private readonly _doc = inject(DOCUMENT);

    constructor() {
        const loggerFactory = inject(LoggerFactory);

        this.logger = loggerFactory.getLogger('SchemaMarkupService');
        this.events = [];
        this.moduleEvents$.pipe(debounceTime(100), takeUntilDestroyed()).subscribe((_) => {
            const activeEvents = this.events.map((event) => event.deref()).filter((event) => event !== undefined);

            this.addEventSchemaMarkup(activeEvents);
        });
    }

    private get document(): Document {
        return this._doc;
    }

    addGenericSchemaMarkup(schemaMarkupType: SchemaMarkupType): HTMLScriptElement {
        const scriptElement = this.document.createElement('script');

        scriptElement.type = `application/ld+json`;
        scriptElement.id = `${SeoMarkupConstants.ldJsonPrefix}${schemaMarkupType}`;

        return scriptElement;
    }

    cleanEvents(): void {
        this.events = [];
    }

    addEventSchemaMarkup(events: (EventModel | Fixture)[]): void {
        this.events = events.map((event) => new WeakRef(event));

        if (events && events.length > 0) {
            const scriptElement = this.addGenericSchemaMarkup(SchemaMarkupType.SportsEvent);

            const eventList: EventModel[] = [];
            const fixtureList: Fixture[] = [];

            for (const event of events) {
                if ('league' in event) {
                    eventList.push(event);
                } else {
                    fixtureList.push(event);
                }
            }

            if (eventList.length > 0) {
                this.generateEventSchema(eventList);
            }

            if (fixtureList.length > 0) {
                this.generateFixtureSchema(fixtureList);
            }

            this.addSchemaMarkupScriptToBody(SchemaMarkupType.SportsEvent, scriptElement);
        }
    }

    updateModuleEvents(fixtures: (EventModel | Fixture)[]) {
        this.events = uniqBy(
            concat(
                this.events,
                fixtures.map((event) => new WeakRef(event)),
            ),
            (f) => f.deref()?.id,
        );

        this.moduleEvents$.next();
    }

    generateLdJson<T extends SeoGenericSchema>(schema: T[] | T): string {
        const ldJson = new SeoLdJsonSchemaContainer(schema);

        return JSON.stringify(ldJson);
    }

    private generateFixtureSchema(events: Fixture[]): string {
        const eventsSchema = events.map((fixture) =>
            this.generateSportEventSchemaObject(
                fixture.name.value,
                this.getIsoStringWithZone(new Date(fixture.startDate)),
                fixture.id,
                fixture.competition.name.value,
                fixture.region.name.value,
                fixture.region.code,
            ),
        );

        return this.generateLdJson(eventsSchema);
    }

    private generateEventSchema(events: EventModel[]): string {
        const eventsSchema = events.map((event) =>
            this.generateSportEventSchemaObject(
                event.name,
                this.getIsoStringWithZone(new Date(event.startDate)),
                event.id,
                event.league.name,
                event.region.name,
                event.region.code || '',
            ),
        );

        return this.generateLdJson(eventsSchema);
    }

    private generateSportEventSchemaObject(
        name: string,
        startDate: string,
        id: string,
        leagueName: string,
        regionName: string,
        regionCode: string,
    ): SeoSportsEventSchema {
        const isRegion = this.getRegion(regionCode);

        return new SeoSportsEventSchema(
            name,
            startDate,
            this.urlHelper.getAbsolute(this.urlHelper.getEventUrl({ id, name })),
            new SeoPlaceSchema(leagueName, new SeoPostalAddressSchema(leagueName, regionName, isRegion)),
        );
    }

    private getRegion(regionCode: string): boolean {
        let isRegion = false;
        if (regionCode && regionCode.toUpperCase() !== SeoMarkupConstants.regionWorldCode) {
            // if region is WORLD then region should be league name
            isRegion = true;
        }

        return isRegion;
    }

    addSchemaMarkupScriptToBody(schemaType: SchemaMarkupType, script: HTMLScriptElement): void {
        try {
            this.removeSchemaMarkupScriptFromBody(schemaType);
            this.document.body.append(script);
        } catch (error) {
            this.logger.error(error, 'Error adding Schema Markup script to view');
        }
    }

    removeSchemaMarkupScriptFromBody(schemaType: SchemaMarkupType): void {
        try {
            const ldJson = this.document.getElementById(`${SeoMarkupConstants.ldJsonPrefix}${schemaType}`);
            if (ldJson) {
                ldJson.remove();
            }
        } catch (error) {
            this.logger.error(error, 'Error Removing Schema Markup script from Body');
        }
    }

    private getIsoStringWithZone(date: Date): string {
        if (!date) {
            return '';
        }
        let timezoneOffSet = date.getTimezoneOffset();
        const negativeOffset = timezoneOffSet < 0;
        timezoneOffSet = Math.abs(timezoneOffSet);
        const offSetHrs = Math.floor(timezoneOffSet / 60);
        const offSetMin = Math.abs(timezoneOffSet % 60);
        let timezoneStandard: string;

        if (timezoneOffSet === 0) {
            timezoneStandard = 'Z';
        } else if (negativeOffset) {
            timezoneStandard = `+${this.padValue(offSetHrs)}:${this.padValue(offSetMin)}`;
        } else {
            timezoneStandard = `-${this.padValue(offSetHrs)}:${this.padValue(offSetMin)}`;
        }

        return date.toISOString().slice(0, 19) + timezoneStandard;
    }

    private padValue(val: number): string {
        return val.toString().padStart(2, '0');
    }
}
